// Install: npm install @mui/material @mui/icons-material @mui/x-data-grid
import React, { useEffect, useMemo, useState, useRef } from "react";
import {
  Box,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
  Paper,
  Typography,
  Button,
  Autocomplete,
  TextField,
  Checkbox,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { styled } from "@mui/material/styles";
import { DataGrid } from "@mui/x-data-grid";
import ExportIcon from "../components/ExportIcon";
import nblReportConfig from "../config/nblReportConfig";
import { DEFAULT_PAGE_SIZE, DEFAULT_PAGE_SIZE_OPTIONS_MUI } from "../config/maintenanceConfig";
import { reportDateSuffix } from "../utils/reportDates";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

/* ======= Styling constants and StyledDataGrid ======= */
const primaryColor = "#005EB8";
const darkGrey = "#D3D3D3";

const StyledDataGrid = styled(DataGrid)(({ theme }) => ({
  "& .MuiDataGrid-columnHeader": {
    "& .MuiDataGrid-columnHeaderTitleContainer": {
      "& .MuiDataGrid-columnHeaderTitle": {
        fontWeight: "bold !important",
        whiteSpace: "normal",
        wordBreak: "break-word",
      },
    },
    backgroundColor: primaryColor,
    color: "#FFFFFF !important",
    fontSize: "0.95rem !important",
    textTransform: "none",
    borderBottom: `2px solid ${primaryColor}`,
  },
  "& .MuiDataGrid-root": {
    height: "600px",
  },
  "& .MuiDataGrid-cell": {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    borderBottom: `1px solid ${darkGrey}`,
  },
  "& .MuiDataGrid-columnHeaders": {
    whiteSpace: "normal",
  },
  "& .editable-cell": {
    backgroundColor: "#FFFDE7 !important",
    "&:hover": {
      backgroundColor: "#FFF9C4 !important",
    },
  },
  "& .MuiDataGrid-row.row-editing .MuiDataGrid-cell--editable": {
    backgroundColor: "#FFF59D !important",
    border: "1px solid #005EB8",
  },
  "& .MuiDataGrid-row.row-editing .MuiDataGrid-cell:not(.MuiDataGrid-cell--editable)": {
    backgroundColor: "#E0E0E0 !important",
  },
}));

/* ======= Column and Filter Templates ======= */
const { reportKey: REPORT_KEY } = nblReportConfig;
// Static fallbacks — overridden at runtime by DB config
const _staticTableTemplate = nblReportConfig.tableTemplate;
const _staticFilterTemplate = nblReportConfig.filterTemplate;

/* ======= Helpers ======= */
// Convert apiKey (snake_case or camelCase) to human readable Title Case
const toHuman = (apiKey) =>
  String(apiKey)
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2") // camelCase -> spaces
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());

// Map order template entries (human names) to API keys using generated human names
const mapOrderTemplateToApiKeys = (orderTemplate, allApiKeys) => {
  const humanMap = {};
  allApiKeys.forEach((k) => {
    humanMap[toHuman(k).toLowerCase()] = k;
  });

  const mapped = [];
  orderTemplate.forEach((entry) => {
    const entryLower = String(entry).toLowerCase().trim();
    // 1) exact match to generated human name
    if (humanMap[entryLower]) {
      mapped.push(humanMap[entryLower]);
      return;
    }
    // 2) entry might already be an api key
    if (allApiKeys.includes(entry)) {
      mapped.push(entry);
      return;
    }
    // 3) fuzzy: remove spaces and compare
    const compact = entryLower.replace(/\s+/g, "");
    const found = allApiKeys.find(
      (k) => toHuman(k).toLowerCase().replace(/\s+/g, "") === compact
    );
    if (found) {
      mapped.push(found);
      return;
    }
    // 4) fallback: keep entry as-is (will be ignored if not present in data)
    mapped.push(entry);
  });

  // preserve order and uniqueness
  return Array.from(new Set(mapped));
};

const toRow = (item, idField = "nbl_main_id") => ({
  id: item[idField] ?? Math.random().toString(36).slice(2, 9),
  ...item,
});

/* ======= Main component ======= */
export default function NBLReport() {
  const baseURI = process.env.REACT_APP_API_BASE_URL ?? "";
  const [loading, setLoading] = useState(true);
  const gridRef = useRef(null);
  const [allRows, setAllRows] = useState([]);
  const [rows, setRows] = useState([]);
  const [availableDates, setAvailableDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState("");
  const [filters, setFilters] = useState({ region: ["India"] });
  const [distinctValues, setDistinctValues] = useState({});
  const [columnOrderApiKeys, setColumnOrderApiKeys] = useState([]);
  const [allApiKeys, setAllApiKeys] = useState([]);
  const [humanReadableMap, setHumanReadableMap] = useState({});
  const [columnVisibilityModel, setColumnVisibilityModel] = useState(() => {
    const saved = localStorage.getItem(REPORT_KEY);
    return saved ? JSON.parse(saved) : {};
  });

  // Live config from DB (falls back to static config)
  const [tableTemplate, setTableTemplate] = useState(_staticTableTemplate);
  const [filterTemplate, setFilterTemplate] = useState(_staticFilterTemplate);
  const [configReady, setConfigReady] = useState(false);
  const ordercolumnsemplate = tableTemplate.map((c) => c.field);
  const filtertemplate = filterTemplate.map((c) => c.field);
  const configHeaderMap = Object.fromEntries(tableTemplate.map((c) => [c.field, c.headerName]));

  // Fetch live config from API on mount
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${baseURI}/api/report-config/${REPORT_KEY}`);
        if (res.ok) {
          const data = await res.json();
          if (Array.isArray(data.table_template) && data.table_template.length > 0) {
            setTableTemplate(data.table_template);
            const hiddenMap = {};
            data.table_template.forEach((c) => { if (c.hidden) hiddenMap[c.field] = false; });
            if (Object.keys(hiddenMap).length > 0) {
              setColumnVisibilityModel((prev) => ({ ...hiddenMap, ...prev }));
            }
          }
          if (Array.isArray(data.filter_template) && data.filter_template.length > 0)
            setFilterTemplate(data.filter_template);
        }
      } catch (_) { /* use static fallback */ } finally {
        setConfigReady(true);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* Initial call to get availableReportDates (and possibly data) */
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${baseURI}/api/pool/supplyData`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ source: "nbl" }),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const resp = await res.json();
        const avail = resp?.availableReportDates ?? [];
        setAvailableDates(avail);
        const defaultDate = avail[0] ?? null;
        if (defaultDate) {
          setSelectedDate(defaultDate);
          await fetchData(defaultDate);
        } else {
          const rowsData = resp?.data ?? resp ?? [];
          const mapped = (rowsData || []).map((r) => toRow(r)).filter((row) => {
            const keys = Object.keys(row).filter(k => k !== 'id');
            return keys.some(k => row[k] !== null && row[k] !== undefined && row[k] !== '');
          });
          setAllRows(mapped);
          setRows(mapped);
        }
      } catch (err) {
        console.error("initial fetch error", err);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* Fetch data for selected date */
  const fetchData = async (date) => {
    setLoading(true);
    try {
      const payload = { source: "nbl", reportExtractionDate: date };
      const res = await fetch(`${baseURI}/api/pool/supplyData`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const resp = await res.json();
      const rowsData = resp?.data ?? [];
      const mapped = (rowsData || []).map((r) => toRow(r)).filter((row) => {
        const keys = Object.keys(row).filter(k => k !== 'id');
        return keys.some(k => row[k] !== null && row[k] !== undefined && row[k] !== '');
      });
      setAllRows(mapped);
      setRows(mapped);
    } catch (err) {
      console.error("fetchData error", err);
      setAllRows([]);
      setRows([]);
      setDistinctValues({});
    } finally {
      setLoading(false);
    }
  };

  /* Build human-readable map, map order template to API keys, and set column order */
  const initMappingsAndColumns = (dataRows, orderTpl = ordercolumnsemplate, headerMap = configHeaderMap) => {
    const sample = dataRows[0] ?? {};
    const apiKeys = Object.keys(sample).filter((k) => k !== "id");

    // Build humanReadableMap: use config headerName if available, else auto-generate
    const hr = {};
    apiKeys.forEach((k) => {
      hr[k] = headerMap[k] ?? toHuman(k);
    });
    setHumanReadableMap(hr);

    // Map order template to api keys using generated human names
    const mappedOrder = mapOrderTemplateToApiKeys(orderTpl, apiKeys);

    // Keep only those mapped keys that actually exist in the data
    const finalOrder = mappedOrder.filter((k) => apiKeys.includes(k));
    
    // Get remaining columns not in the template
    const remainingKeys = apiKeys.filter((k) => !finalOrder.includes(k));
    
    // Combine: ordered columns first, then remaining
    const allOrderedKeys = [...finalOrder, ...remainingKeys];
    setColumnOrderApiKeys(allOrderedKeys);
    setAllApiKeys(apiKeys);
  };

  /* Compute distinct values for filtertemplate (client-side) */
  const computeDistinctValues = (dataRows, fltTpl = filtertemplate) => {
    const dv = {};
    fltTpl.forEach((apiKey) => {
      const setVals = new Set();
      dataRows.forEach((r) => {
        const val = r[apiKey];
        if (val !== null && val !== undefined) setVals.add(String(val));
      });
      dv[apiKey] = Array.from(setVals).sort();
    });
    setDistinctValues(dv);
  };

  /* Re-apply column order + headers + filter values whenever data or config changes */
  useEffect(() => {
    if (!allRows.length) return;
    const newOrderTpl = tableTemplate.map((c) => c.field);
    const newHeaderMap = Object.fromEntries(tableTemplate.map((c) => [c.field, c.headerName]));
    const newFltTpl = filterTemplate.map((c) => c.field);
    initMappingsAndColumns(allRows, newOrderTpl, newHeaderMap);
    computeDistinctValues(allRows, newFltTpl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allRows, tableTemplate, filterTemplate]);

  /* Apply client-side filters - supports multi-select */
  useEffect(() => {
    if (!allRows.length) {
      setRows([]);
      return;
    }
    let filtered = [...allRows];
    Object.entries(filters).forEach(([apiKey, values]) => {
      if (!values || !Array.isArray(values) || values.length === 0) return;
      filtered = filtered.filter((r) => {
        const cell = r[apiKey];
        if (cell === null || cell === undefined) return false;
        return values.includes(String(cell));
      });
    });
    setRows(filtered);
  }, [filters, allRows]);

  /* Build columns array — include all columns (ordered first, then remaining) */
  const columns = useMemo(() => {
    // Calculate optimal width based on header and data content
    const getColumnWidth = (key) => {
      const headerText = humanReadableMap[key] ?? toHuman(key);
      const headerWidth = headerText.length * 9 + 40; // Approx char width + padding
      
      // Sample data to find max content width (check first 100 rows for performance)
      let maxDataWidth = 0;
      const sampleRows = allRows.slice(0, 100);
      sampleRows.forEach((row) => {
        const cellValue = row[key];
        if (cellValue !== null && cellValue !== undefined) {
          const cellWidth = String(cellValue).length * 8 + 20;
          maxDataWidth = Math.max(maxDataWidth, cellWidth);
        }
      });
      
      // Return the larger of header or data width, with min/max bounds
      const calculatedWidth = Math.max(headerWidth, maxDataWidth);
      return Math.min(Math.max(calculatedWidth, 80), 300); // Min 80, Max 300
    };

    return columnOrderApiKeys.map((key) => ({
      field: key,
      headerName: humanReadableMap[key] ?? toHuman(key),
      width: getColumnWidth(key),
      sortable: true,
    }));
  }, [columnOrderApiKeys, humanReadableMap, allRows]);

  /* Save column visibility to localStorage */
  const handleColumnVisibilityChange = (newModel) => {
    setColumnVisibilityModel(newModel);
    localStorage.setItem(REPORT_KEY, JSON.stringify(newModel));
  };

  /* Handlers */
  const onDateChange = async (e) => {
    const date = e.target.value;
    setSelectedDate(date);
    await fetchData(date);
  };

  const handleFilterChange = (apiKey, value) => {
    setFilters((prev) => ({ ...prev, [apiKey]: value }));
  };

  return (
    <Box p={1}>
      {/* Full Screen Loader */}
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading || !configReady}
      >
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
          <CircularProgress color="inherit" size={60} />
          <Typography variant="h6" color="inherit" sx={{ textAlign: "center" }}>
            Loading NBL Report...
          </Typography>
        </Box>
      </Backdrop>

      {/* Top: Filters and Report Date */}
      <Paper sx={{ p: 1, mb: 1 }}>
        <Grid container spacing={1} alignItems="center">
          <Grid item xs={12} md={7}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: "bold",
                color: primaryColor,
                marginBottom: 2,
                textAlign: "left",
              }}
            >
              Supply - NBL
            </Typography>
          </Grid>

          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel id="report-date-label">Report Date</InputLabel>
              <Select
                labelId="report-date-label"
                value={selectedDate || ""}
                label="Report Date"
                onChange={onDateChange}
                size="small"
              >
                {availableDates.map((d) => (
                  <MenuItem key={d} value={d}>
                    {d}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={3} sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1 }}>
            <ExportIcon
              exportRows={rows}
              exportColumns={columns}
              exportTableRef={gridRef}
              reportName={`NBL_${reportDateSuffix()}`}
            />
            <Button
              variant="outlined"
              size="small"
              onClick={() => setFilters({})}
              sx={{ height: 36 }}
            >
              Clear Filters
            </Button>
          </Grid>

          {/* Filters inline to the right of date on larger screens */}
          <Grid item xs={12} md={12}>
            <Grid container spacing={1}>
              {filterTemplate.map(({ field, headerName }) => {
                const options = distinctValues[field] ?? [];
                return (
                  <Grid item xs={12} sm={6} md={3} key={field}>
                    <Autocomplete
                      multiple
                      size="small"
                      options={options}
                      disableCloseOnSelect
                      getOptionLabel={(option) => option}
                      value={filters[field] ?? []}
                      onChange={(event, newValue) => handleFilterChange(field, newValue)}
                      renderOption={(props, option, { selected }) => (
                        <li {...props}>
                          <Checkbox
                            icon={icon}
                            checkedIcon={checkedIcon}
                            style={{ marginRight: 8 }}
                            checked={selected}
                          />
                          {option}
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField {...params} label={headerName} placeholder={headerName} />
                      )}
                      sx={{ minWidth: 200 }}
                    />
                  </Grid>
                );
              })}
            </Grid>
          </Grid>
        </Grid>
      </Paper>

      {/* Bottom: DataGrid (only columns from ordercolumnsemplate are shown) */}
   <Paper sx={{ height: 450, p: 1 }}>
        <StyledDataGrid
          ref={gridRef}
          rows={rows}
          columns={columns}
          pageSize={DEFAULT_PAGE_SIZE}
          rowsPerPageOptions={DEFAULT_PAGE_SIZE_OPTIONS_MUI}
          loading={loading}
          disableSelectionOnClick
          autoHeight={false}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={handleColumnVisibilityChange}
          sx={{ border: `1px solid ${darkGrey}` }}
        />
      </Paper>
    </Box>
  );
}
